import { TALK_VERB, TRAVEL_VERB } from "odyssey-shared";
import { say } from "../../words.ts";
import type { InteractContext, InteractKind } from "../interact-context.ts";
import { verbLabel } from "../world-ui/prompt/label.ts";
import type { TouchIconName } from "./icons.ts";

/**
 * What the touch Interact button shows: the action's word, its icon, and whether nothing is in reach, when the
 * button is dimmed.
 */
export interface InteractView {
  readonly label: string;
  readonly icon: TouchIconName;
  readonly dimmed: boolean;
}

/**
 * The icon a contract verb is drawn with; a verb with none of its own gets the gear, the generic "use".
 */
const VERB_ICONS: Readonly<Record<string, TouchIconName>> = { [TALK_VERB]: "talk", [TRAVEL_VERB]: "enter", read: "read" };

/**
 * The Interact button's view for what E would do now (`InteractControl.context`, the one choice the key and the world
 * prompt read), so the button's word is the prompt's word.
 *
 * @param context What E would do.
 * @param doorWord The door's word, "Enter".
 * @returns The label and icon, dimmed when nothing is in reach or the thing is not yet to be used.
 * @example
 * interactView({ kind: "door", verb: "" }, "Enter");
 * // { label: "Enter", icon: "enter", dimmed: false }
 */
export function interactView(context: InteractContext, doorWord: string = say("door.enterUnnamed")): InteractView {
  switch (context.kind) {
    case "verb":
      return { label: verbLabel(context.verb), icon: VERB_ICONS[context.verb] ?? "use", dimmed: false };
    case "harvest":
      return { label: say("harvest.action"), icon: "harvest", dimmed: false };
    case "door":
      return { label: doorWord, icon: "enter", dimmed: false };
    case "pickUp":
      return { label: say("touch.pickUp"), icon: "pickup", dimmed: false };
    case "waiting":
    case "none":
      return { label: say("touch.interact"), icon: "use", dimmed: true };
  }
}

/**
 * An `InteractView` written in place, with the choice it was written for, so a frame that finds the same choice changes
 * and allocates nothing.
 */
export interface MutableInteractView {
  label: string;
  icon: TouchIconName;
  dimmed: boolean;
  kind: InteractKind | "unset";
  verb: string;
}

/**
 * A view to write into, written for nothing in reach.
 *
 * @returns The view.
 */
export function newInteractView(): MutableInteractView {
  const view: MutableInteractView = { label: "", icon: "use", dimmed: true, kind: "unset", verb: "" };
  interactViewInto(view, { kind: "none", verb: "" });
  return view;
}

/**
 * Writes the Interact button's view for what E would do now into `out`; a choice the view was already written for changes
 * nothing, so a frame of steady play allocates nothing. The words are made only when the choice changes (`writeInteractView`).
 *
 * @param out The view.
 * @param context What E would do.
 */
export function interactViewInto(out: MutableInteractView, context: InteractContext): void {
  if (out.kind === context.kind && out.verb === context.verb) {
    return;
  }
  writeInteractView(out, context);
}

/**
 * Writes the view for a new choice: its word, its icon and whether it is dimmed.
 *
 * @param out The view.
 * @param context What E would do.
 */
function writeInteractView(out: MutableInteractView, context: InteractContext): void {
  const view = interactView(context);
  out.label = view.label;
  out.icon = view.icon;
  out.dimmed = view.dimmed;
  out.kind = context.kind;
  out.verb = context.verb;
}
